import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useImportRedirect } from "@/hooks/useImportRedirect"
import { supabase } from "@/lib/supabase"
import { useSiteStyles, FONT_SIZE_MAP } from "@/hooks/useGoogleFont"
import { useNavigation } from "@/hooks/useNavigation"
import { getTemplateComponent } from "@/templates"
import { siteCssVars } from "@/lib/designTokens"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { sortPages } from "@/stores/customizationStore"
import { PublicFooter } from "@/components/public/PublicFooter"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { resolvePageHead } from "@/lib/seo/headData"
import { nonprofitOrganizationJsonLd } from "@/lib/seo/structuredData"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"

interface PageSummary {
  id: string
  slug: string
  title: string | null
  page_type?: string | null
}

interface ActivePageSeo {
  slug: string
  title: string | null
  /** NOS-181: SEO overrides (source metadata at import; editable in settings) */
  seo_title: string | null
  seo_description: string | null
  meta: Record<string, unknown>
  updated_at: string
}

export function PublicWebsitePage() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isPreview = searchParams.get("preview") === "true"
  /**
   * NOS-58: prefer the path over the ?page= query param. NOS-241: the slug is
   * the FULL remaining path (not just one segment) so nested imported slugs
   * ("/about-us/impact") resolve, and the /:orgSlug/* catch-all can hand stale
   * multi-segment source URLs to the redirect map below.
   */
  const restPath = pathname.split("/").filter(Boolean).slice(1).join("/")
  const pageSlug = restPath || searchParams.get("page") || "home"
  const [org, setOrg] = useState<Organization | null>(null)
  const [customizations, setCustomizations] = useState<OrgCustomizations | null>(null)
  const [sections, setSections] = useState<PageSection[]>([])
  const [pages, setPages] = useState<PageSummary[]>([])
  const [templateName, setTemplateName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [activePageSeo, setActivePageSeo] = useState<ActivePageSeo | null>(null)

  // NOS-241: when the page lookup 404s, consult the org's imported redirect
  // map (source path → new path) before showing "Page not found."
  const pageNotFound = !loading && !notFound && org != null && activePageSeo == null
  const redirect = useImportRedirect(org?.id, `/${restPath}`, pageNotFound)
  useEffect(() => {
    if (redirect.status === "found" && org) {
      const target = redirect.target === "/" ? `/${org.slug}` : `/${org.slug}${redirect.target}`
      navigate(target, { replace: true })
    }
  }, [redirect, org, navigate])

  const { items: navItems } = useNavigation(org?.id ?? null)
  const bodyFont = customizations?.body_font || customizations?.font_family || "Inter, sans-serif"
  const headingFont = customizations?.heading_font || bodyFont
  useSiteStyles(headingFont, bodyFont, customizations?.font_size ?? "medium")

  useEffect(() => {
    if (!orgSlug) {
      setLoading(false)
      setNotFound(true)
      return
    }

    let cancelled = false

    async function load() {
      const slug = orgSlug ?? ""
      if (!slug) return
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select(
          "id, name, slug, mission_statement, logo_url, custom_domain, legal_name, mailing_address, founding_year"
        )
        .eq("slug", slug)
        .maybeSingle()

      if (cancelled || orgError || !orgData) {
        if (!cancelled) {
          setNotFound(true)
          setLoading(false)
        }
        return
      }

      const orgRow = orgData as Organization
      setOrg(orgRow)

      const { data: custData, error: custErr } = await supabase
        .from("org_customizations")
        .select("primary_color, secondary_color, font_family, heading_font, body_font, font_size, sticky_nav, template_id, template_version, nav_cta_text, logo_scale, design_tokens, custom_css, announcement")
        .eq("org_id", orgRow.id)
        .maybeSingle()

      if (cancelled) return
      const cust = custErr ? null : (custData as OrgCustomizations | null)
      if (cust) setCustomizations(cust)

      let name: string | null = null
      if (cust?.template_id) {
        const { data: templateData } = await supabase
          .from("templates")
          .select("name")
          .eq("id", cust.template_id)
          .maybeSingle()
        if (templateData && typeof (templateData as { name: string }).name === "string") {
          name = (templateData as { name: string }).name
        }
      }
      if (!cancelled) setTemplateName(name ?? null)

      // Try both slug formats: AI-imported pages store slugs with a leading slash
      // (e.g. "/support-for-women") while URL params and manually created pages omit it.
      const slugsToTry = pageSlug === "home"
        ? ["home"]
        : [pageSlug, `/${pageSlug}`]

      type PageDataRow = {
        id: string
        slug: string
        title: string | null
        seo_title: string | null
        seo_description: string | null
        meta: unknown
        updated_at: string
      }
      let pageData: PageDataRow | null = null
      for (const candidateSlug of slugsToTry) {
        let q = supabase
          .from("pages")
          .select("id, slug, title, seo_title, seo_description, meta, updated_at")
          .eq("org_id", orgRow.id)
          .eq("slug", candidateSlug)
        if (!isPreview) q = q.eq("is_published", true)
        const { data, error } = await q.maybeSingle()
        if (!cancelled && !error && data) {
          pageData = data as unknown as PageDataRow
          break
        }
      }

      if (cancelled || !pageData) {
        if (!cancelled) {
          setActivePageSeo(null)
          setLoading(false)
        }
        return
      }

      const metaObj =
        pageData.meta && typeof pageData.meta === "object" && !Array.isArray(pageData.meta)
          ? (pageData.meta as Record<string, unknown>)
          : {}
      if (!cancelled) {
        setActivePageSeo({
          slug: pageData.slug,
          title: pageData.title,
          seo_title: pageData.seo_title,
          seo_description: pageData.seo_description,
          meta: metaObj,
          updated_at: pageData.updated_at,
        })
      }

      const pageId = pageData.id
      const { data: sectionsData, error: sectionsError } = await supabase
        .from("page_sections")
        .select("id, section_type, order_index, content, page_id, org_id, is_visible")
        .eq("page_id", pageId)
        .order("order_index", { ascending: true })

      let pagesQuery = supabase
        .from("pages")
        .select("id, slug, title, page_type")
        .eq("org_id", orgRow.id)
      if (!isPreview) {
        pagesQuery = pagesQuery.eq("is_published", true)
      }
      const { data: pagesData } = await pagesQuery

      if (cancelled) return
      // Same ordering as the builder nav (home → about → events → blog → contact → custom)
      if (pagesData) setPages(sortPages(pagesData as PageSummary[]))
      if (!sectionsError && sectionsData) {
        setSections(sectionsData as PageSection[])
      } else {
        setSections([])
      }
      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgSlug, isPreview, pageSlug])

  // NOS-249: title/description/canonical/og:image resolution moved into the
  // shared renderer-agnostic resolver (src/lib/seo/headData.ts). Pages
  // without an explicit meta.og_image get the generated branded default.
  const headData = useMemo(() => {
    if (!org) return null
    const segments = pathname.split("/").filter(Boolean)
    const sitePath = segments.length > 1 ? `/${segments.slice(1).join("/")}` : "/"
    return resolvePageHead({ org, page: activePageSeo, sitePath, sections })
  }, [org, activePageSeo, pathname, sections])

  // NOS-242: regenerated NonprofitOrganization JSON-LD from NOS's own org
  // model (never copied from the source site). EIN is intentionally NOT
  // emitted — there is no public-display opt-in yet, and sponsored/transferred
  // orgs (M25) could otherwise leak a wrong-entity taxID.
  const jsonLd = useMemo(() => {
    if (!org || !activePageSeo || isPreview) return []
    if (activePageSeo.slug !== "home") return []
    return [
      nonprofitOrganizationJsonLd({
        name: org.name,
        url: getPublicSiteOrigin(org),
        logoUrl: org.logo_url,
        missionStatement: org.mission_statement,
        legalName: org.legal_name,
        address: org.mailing_address,
        foundingYear: org.founding_year,
      }),
    ]
  }, [org, activePageSeo, isPreview])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (notFound || !org) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Organization not found.</p>
      </div>
    )
  }

  if (!activePageSeo) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">
          {redirect.status === "checking" ? "Loading..." : "Page not found."}
        </p>
      </div>
    )
  }

  const defaults: OrgCustomizations = {
    primary_color: "#3b82f6",
    secondary_color: "#1e40af",
    font_family: "Inter, sans-serif",
    template_id: null,
  }
  const cust = customizations ?? defaults
  const TemplateComponent = getTemplateComponent(templateName)
  const primaryColor = cust.primary_color ?? "#3b82f6"
  const navFontFamily = cust.body_font || cust.font_family || "Inter, sans-serif"
  const custFontSizeBase = FONT_SIZE_MAP[cust.font_size ?? "medium"] ?? "16px"

  const getPagePath = (slug: string) => {
    const base = `/${org.slug}`
    if (slug === "home") return `${base}${isPreview ? "?preview=true" : ""}`
    return `${base}/${slug}${isPreview ? "?preview=true" : ""}`
  }

  const siteContent = (
    <div
      className="nos-site-root"
      style={{
        all: "initial",
        fontFamily: navFontFamily,
        fontSize: custFontSizeBase,
        display: "block",
        ...siteCssVars(cust),
        ["--primary-color" as string]: primaryColor,
        ["--secondary-color" as string]: cust.secondary_color ?? "#1e40af",
        ["--font-family" as string]: navFontFamily,
      }}
    >
      {headData ? (
        <SEOHead
          title={headData.title}
          description={headData.description}
          canonicalUrl={headData.canonicalUrl}
          ogType={headData.ogType}
          imageUrl={headData.ogImage}
          jsonLd={jsonLd.length ? jsonLd : null}
          robotsNoindex={isPreview}
        />
      ) : null}
      <PublicNavBar
        orgName={org.name ?? "Organization"}
        logoUrl={org.logo_url}
        navItems={navItems}
        pages={pages}
        primaryColor={primaryColor}
        navCtaText={cust.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(cust.announcement)}
        fontFamily={navFontFamily}
        getPagePath={getPagePath}
        orgId={org.id}
        logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
        stickyNav={cust.sticky_nav ?? true}
      />
      <TemplateComponent org={org} customizations={cust} sections={sections} isHomePage={activePageSeo.slug === "home"} />
      {cust.custom_css ? <style>{cust.custom_css}</style> : null}
      <PublicFooter
        orgId={org.id}
        orgName={org.name ?? "Organization"}
        logoUrl={org.logo_url}
        missionStatement={org.mission_statement}
        primaryColor={primaryColor}
        secondaryColor={cust.secondary_color ?? "#1e40af"}
        fontFamily={navFontFamily}
        pages={pages}
        getPagePath={getPagePath}
        logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
      />
    </div>
  )

  return (
    <div
      className="min-h-screen scroll-smooth"
      style={
        {
          fontFamily: navFontFamily,
          ["--primary-color" as string]: primaryColor,
          ["--secondary-color" as string]: cust.secondary_color ?? "#1e40af",
        } as React.CSSProperties
      }
    >
      {isPreview && (
        <div className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
          <span>Preview Mode</span>
          <span className="text-amber-800">— Unpublished changes</span>
        </div>
      )}
      {siteContent}
    </div>
  )
}
